import './style.scss'
import { Button, Drawer, Input, message, Modal, Progress, Radio, Space, Spin } from 'antd'
import { RcFile } from 'antd/es/upload'
import Dragger from 'antd/es/upload/Dragger'
import axios from 'axios'
import { FC, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cmsApi } from '@/api'
import * as homeApi from '@/api/home'
import error1 from '@/assets/error1.png'
import error2 from '@/assets/error2.png'
import error3 from '@/assets/error3.png'
import error4 from '@/assets/error4.png'
import SystemDeclare from '@/components/Declare'
import VideoModal from '@/components/VideoModal'
import { UserStore } from '@/global-states'
import { eventTracking, showDeclare, showPlanModal } from '@/libs/util'
import { CloudUploadOutlined, DeleteOutlined, ExclamationCircleFilled } from '@ant-design/icons'

interface IProps {
  open: boolean
  onCancel?: () => void
  onOk?: () => void
  group: any
}

const CreatePhotoAvatarDrawer: FC<IProps> = (props) => {
  const { open, group, onCancel, onOk } = props
  const { t } = useTranslation(['system', 'avatar', 'material'])
  const { declareChecked } = UserStore
  const [currentStep, setCurrentStep] = useState(0)
  const [photoFile, setPhotoFile] = useState<RcFile>(undefined as any)
  const [photoUrl, setPhotoUrl] = useState<string>(undefined as any)
  const [photoInfo, setPhotoInfo] = useState<any>({})
  const [percent, setPercent] = useState(0)
  const cancelTokenSource = useRef<any>()
  const [title, setTitle] = useState('')
  const [guideModalOpen, setGuideModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<any>()
  const [mode, setMode] = useState<any>()
  const [pictureInfo, setPictureInfo] = useState<any>()

  const modeInfo = useMemo(() => {
    if (pictureInfo) {
      return pictureInfo?.provider_info_list?.find((l: any) => l.provider === mode)
    }
    return
  }, [pictureInfo, mode])

  useEffect(() => {
    if (open) {
      setMode(2)
      restartUpload()
      if (!group?.id) {
        getPictureInfo()
      }

      if (group?.id) {
        setTitle(`${group.title}-${t('avatar.photo_material')}${(group.member_count || 0) + 1}`)
      } else {
        setTitle('')
      }
    }
  }, [open])

  useEffect(() => {
    if (photoFile) {
      setCurrentStep(1)
      setPhotoUrl(URL.createObjectURL(photoFile))
    } else {
      setPhotoUrl(undefined as any)
    }
  }, [photoFile])

  const onGuideModalClose = () => {
    setGuideModalOpen(false)
  }

  const getPictureInfo = async () => {
    const res = await homeApi.getPictureAvatarInfo()
    setPictureInfo(res)
  }

  const beforeVideoUpload = async (file: RcFile) => {
    return new Promise((resolve) => {
      const type = file?.name?.split(/\./)?.slice(-1)?.[0]?.toLocaleLowerCase() as string
      if (['png', 'jpg', 'jpeg'].includes(type)) {
        const size = file?.size || 0
        if (size / 1000 / 1000 > 10) {
          message.warning(t('avatar.photo_size_limit'))
          resolve(false)
        }
      } else {
        message.warning(t('avatar.photo_format_limit'))
        resolve(false)
      }
      resolve(true)
    })
  }

  const onDrop = async (file: RcFile) => {
    const type = file?.name?.split(/\./)?.slice(-1)?.[0]?.toLocaleLowerCase() as string
    if (!['png', 'jpg', 'jpeg'].includes(type)) {
      message.warning(t('avatar.photo_format_limit'))
    }
  }

  // media_type -  0 未定义  1 视频  2 音频 3 图片
  const uploadFile = async (file: RcFile, media_type: 0 | 1 | 2 | 3) => {
    try {
      const segs = (file.name || '').split(/\./)
      const { upload_url, oss_key, content_type } =
        (await cmsApi.post('upload_url', {
          extension: segs[segs.length - 1],
          media_type
        })) || {}
      if (!upload_url) {
        throw new Error(t('system.upload_failed'))
      }

      cancelTokenSource.current = axios.CancelToken.source()
      setPercent(0)
      setCurrentStep(1)

      await cmsApi.upload(upload_url.replace(/^http:\/\//, 'https://').replace('-internal', ''), file, {
        onUploadProgress: (progress) => {
          const percent = Math.round((progress.progress || 0) * 100)
          setPercent(percent)
        },
        headers: {
          'Content-Type': content_type
        },
        cancelToken: cancelTokenSource.current?.token
      })

      setCurrentStep(2)
      setPhotoInfo({
        title: segs[0],
        oss_key,
        file
      })
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        message.error(err?.message || err)
      }
    }
  }

  const restartUpload = () => {
    cancelUpload()
    setCurrentStep(0)
    setPercent(0)
    setPhotoFile(undefined as any)
    setPhotoInfo(undefined)
  }

  const cancelUpload = () => {
    if (cancelTokenSource) {
      cancelTokenSource.current?.cancel(t('system.cancel_upload'))
    }
  }

  const onModeChange = (e: any) => {
    const v = e.target.value
    setMode(v)
  }

  const completeCreate = async () => {
    if (!declareChecked) {
      await showDeclare()
    }

    if (!photoInfo?.oss_key) {
      return message.warning(t('avatar.upload_photo_first'))
    }

    if (!title) {
      return message.warning(group?.id ? t('avatar.input_material_name') : t('avatar.input_avatar_name'))
    }

    submitCreate()
  }

  const submitCreate = async (form?: any, ignoreFace?: boolean) => {
    eventTracking('InstantCloneConfirm')
    setLoading(true)

    const res = await homeApi.faceExtraction({
      oss_key: photoInfo.oss_key
    })

    try {
      const params: any = {
        oss_key: photoInfo?.oss_key,
        source_type: 3,
        live_portrait_provider: group?.id ? undefined : mode,
        ignore_no_face_warning: ignoreFace
      }

      if (group?.id) {
        params.title = title
        params.with_group = {
          group_id: group.id
        }
      } else {
        params.with_new_group = {
          title,
          face_oss_key: res?.extracted_face?.oss_key
        }
      }
      await homeApi.addDigitalHumans(params)
      message.success(t('system.submit_success'))
      onCancel?.()
      onOk?.()
    } catch (error: any) {
      if (error.code === 1014) {
        eventTracking('AvatarCloneLimit')
        Modal.confirm({
          title: t('avatar.avatar_limit_reached'),
          content: <div>{t('avatar.free_user_limit')}</div>,
          okText: t('system.upgrade_member'),
          cancelText: t('system.cancel'),
          onOk: () => {
            showPlanModal()
          }
        })
      }

      if (error.code === 1024) {
        Modal.confirm({
          title: t('avatar.no_face_detected'),
          content: <div>{t('avatar.no_face_warning')}</div>,
          okText: t('avatar.continue_generate'),
          cancelText: t('avatar.reupload'),
          onOk: () => {
            submitCreate(form, true)
          },
          onCancel: () => {
            restartUpload()
          }
        })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Suspense>
      <Drawer
        className="create-photo-drawer"
        open={open}
        title={group?.id ? t('avatar.add_photo_material') : t('avatar.create_avatar_from_photo')}
        width={740}
        onClose={onCancel}
        footer={
          <>
            <SystemDeclare />
            <Space>
              {modeInfo && !group?.id && (
                <div className="fee">
                  <div className="score">
                    {!modeInfo.discounted_credits && (
                      <>
                        <label className="through">
                          {modeInfo.origin_credits}
                          {t('avatar.credit_times')}
                        </label>
                        <label className="light">{t('avatar.free_trial')}</label>
                      </>
                    )}
                    {modeInfo.discounted_credits && (
                      <>
                        {modeInfo.origin_credits === modeInfo.discounted_credits ? (
                          <label className="light">
                            {modeInfo.origin_credits}
                            {t('avatar.credit_times')}
                          </label>
                        ) : (
                          <>
                            <label className="through">
                              {modeInfo.origin_credits}
                              {t('avatar.credit_times')}
                            </label>
                            <label className="light">
                              {modeInfo.discounted_credits}
                              {t('avatar.credit_times')}
                            </label>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
              <Button onClick={onCancel}>{t('system.cancel')}</Button>
              <Button type="primary" loading={loading} onClick={completeCreate}>
                {loading ? t('system.submitting') : t('system.submit')}
              </Button>
            </Space>
          </>
        }
      >
        <Spin spinning={loading} tip={t('system.submitting')}>
          <div className="main">
            <div className="form-item">
              <div className="title">{group?.id ? t('avatar.material_name') : t('avatar.avatar_name')}</div>
              <Input
                maxLength={80}
                placeholder={group?.id ? t('avatar.input_material_name') : t('avatar.input_avatar_name')}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="form-item">
              <div className="title">
                {t('system.upload_photo')}
                <ExclamationCircleFilled
                  onClick={() => {
                    eventTracking('InstantCloneTips')
                    setGuideModalOpen(true)
                  }}
                />
              </div>

              {currentStep === 0 && (
                <>
                  <div className="warning">
                    <h4>{t('avatar.photo_requirements')}</h4>
                    <div className="desc">
                      <div>
                        <label className="label">{t('avatar.person')}：</label>
                        <label>{t('avatar.front_half_body')}</label>
                      </div>
                      <div>
                        <label className="label">{t('avatar.format')}：</label>
                        <label>png/jpg/jpeg</label>
                      </div>
                      <div>
                        <label className="label">{t('avatar.limitations')}：</label>
                        <label>{t('avatar.no_multiple_faces')}</label>
                      </div>
                      <div className="w200">
                        <label className="label">{t('avatar.size')}：</label>
                        <label>{t('avatar.size_limit')}</label>
                      </div>
                    </div>
                  </div>
                  <div className="content">
                    <Dragger
                      accept=".png,.PNG,.jpg,.JPG,.jepg,.JPEG"
                      showUploadList={false}
                      beforeUpload={async (file) => {
                        const flag = (await beforeVideoUpload(file)) as any
                        if (flag) {
                          setPhotoFile(file)
                          uploadFile(file, 3)
                        }
                        return flag
                      }}
                      onDrop={(e) => onDrop(e.dataTransfer.files?.[0] as any)}
                    >
                      <p className="ant-upload-drag-icon">
                        <CloudUploadOutlined />
                      </p>
                      <p className="ant-upload-text">
                        {t('avatar.upload_photo_prompt')}
                        {!group?.id && <label>，{t('avatar.for_avatar_creation')}</label>}
                      </p>
                      <p className="ant-upload-hint">{t('system.upload_hint')}</p>
                    </Dragger>
                  </div>
                </>
              )}
              {currentStep === 1 && (
                <div className="step-progress">
                  <div className="step-progress-content">
                    <div className="percent">{percent}%</div>
                    <Progress percent={percent} showInfo={false} />
                    <div className="tips">{t('system.uploading_photo')}</div>
                  </div>
                  <div className="btns">
                    <Button onClick={restartUpload}>{t('system.cancel')}</Button>
                  </div>
                </div>
              )}
              {currentStep === 2 && (
                <div className="step-view">
                  <div className="step-view-box video">
                    <img src={photoUrl} />
                    <div className="trash" onClick={restartUpload}>
                      <DeleteOutlined />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {!group?.id && (
              <div className="form-item">
                <div className="title">{t('avatar.select_model')}</div>

                <Radio.Group onChange={onModeChange} value={mode}>
                  <Radio value={2}>
                    <strong>{t('avatar.image_to_video_v2.1')}</strong>
                  </Radio>
                  <Radio value={1}>
                    <strong>{t('avatar.image_to_video_v2.0')}</strong>
                  </Radio>
                </Radio.Group>
              </div>
            )}

            <Modal
              title={t('avatar.photo_requirements')}
              open={guideModalOpen}
              footer={
                <Button type="primary" onClick={onGuideModalClose}>
                  {t('system.i_know')}
                </Button>
              }
              closeIcon={false}
              width={580}
              onCancel={onGuideModalClose}
            >
              <div className="modal-guide">
                <ul>
                  <li>
                    <label>1.</label>
                    {t('avatar.ensure_frontal')}
                  </li>
                  <li>
                    <label>2.</label>
                    {t('avatar.no_multiple_faces')}
                  </li>
                  <li>
                    <label>3.</label>
                    {t('avatar.face_not_too_small')}
                  </li>
                  <li>
                    <label>4.</label>
                    {t('avatar.face_not_too_large')}
                  </li>
                  <li>
                    <label>5.</label>
                    {t('avatar.no_face_obstruction')}
                  </li>
                  <li>
                    <label>6.</label>
                    {t('avatar.size_and_dimension_limit')}
                  </li>
                </ul>
                <div className="guide-error">
                  <div className="title-error">{t('avatar.error_examples')}</div>
                  <div className="error-list">
                    <div className="item">
                      <img className="img1" src={error1} />
                      <div>{t('avatar.multiple_faces')}</div>
                    </div>
                    <div className="item">
                      <img className="img2" src={error2} />
                      <div>{t('avatar.face_too_large')}</div>
                    </div>
                    <div className="item">
                      <img className="img3" src={error3} />
                      <div>{t('avatar.face_obstructed')}</div>
                    </div>
                    <div className="item">
                      <img className="img3" src={error4} />
                      <div>{t('avatar.no_face_detected')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Modal>
          </div>
        </Spin>

        <VideoModal preview={preview} onCancel={() => setPreview(undefined)} />
      </Drawer>
    </Suspense>
  )
}

export default CreatePhotoAvatarDrawer
